import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "../../../supabase/supabase";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function TranscriptInput() {
  const [transcript, setTranscript] = useState("");
  const [transcriptName, setTranscriptName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!transcript.trim()) {
      toast({
        title: "Error",
        description: "Please enter a transcript.",
        variant: "destructive",
      });
      return;
    }

    if (!transcriptName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a name for the transcript.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Get the current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Get the user's organization
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("organization_id")
        .eq("id", user.id)
        .single();

      if (userError) throw userError;
      if (!userData.organization_id)
        throw new Error("User has no organization");

      // Save the transcript
      const { data: transcriptData, error: transcriptError } = await supabase
        .from("transcripts")
        .insert({
          user_id: user.id,
          organization_id: userData.organization_id,
          name: transcriptName,
          content: transcript,
        })
        .select()
        .single();

      if (transcriptError) throw transcriptError;

      toast({
        title: "Success",
        description: "Transcript saved successfully.",
      });

      // Navigate to create prompt with this transcript
      navigate(`/prompt-engine/create?transcriptId=${transcriptData.id}`);
    } catch (error) {
      console.error("Error saving transcript:", error);
      toast({
        title: "Error",
        description: "Failed to save transcript. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center mb-2">
          <Button
            variant="ghost"
            size="sm"
            className="mr-2 -ml-2"
            onClick={() => navigate("/prompt-engine")}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
        <CardTitle className="text-xl">Add New Transcript</CardTitle>
        <CardDescription>
          Paste your conversation transcript below. This will be used to
          generate an AI prompt.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label
            htmlFor="transcript-name"
            className="block text-sm font-medium mb-1"
          >
            Transcript Name
          </label>
          <Input
            id="transcript-name"
            placeholder="Enter a name for this transcript"
            value={transcriptName}
            onChange={(e) => setTranscriptName(e.target.value)}
          />
        </div>
        <div>
          <label
            htmlFor="transcript-content"
            className="block text-sm font-medium mb-1"
          >
            Transcript Content
          </label>
          <Textarea
            id="transcript-content"
            placeholder="Paste your conversation transcript here..."
            className="min-h-[300px] font-mono text-sm"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
          />
        </div>
      </CardContent>
      <CardFooter className="flex justify-end space-x-4">
        <Button
          variant="outline"
          onClick={() => navigate("/prompt-engine")}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            isSubmitting || !transcript.trim() || !transcriptName.trim()
          }
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Continue to Prompt Generation"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
